import { AbstractDownloadableService } from './abstract-downloadable.service';
import {
    Guest,
    Reservation
} from '../models';
import { mapGetToPostParams } from "../../app/helpers";

/**
 * @class GuestPmsApiService
 * @abstract
 * @extends {AbstractDownloadableService<Guest>}
 */
export class GuestPmsApiService extends AbstractDownloadableService {

    static get $inject() {

        return super.$inject.concat(['Upload']);
    }

    /**
     * @type {string}
     * @protected
     */
    get urlPattern() {

        return '/property/:propertyId/guests(/:id)';
    }

    /**
     * @param {Object.<string, *>} attributes
     * @returns {Guest}
     * @protected
     */
    newModel(attributes, ...params) {

        return new Guest(attributes);
    }

    constructor(http, config, eventsBroker, DownloadFile, Upload) {

        super(http, config, eventsBroker, DownloadFile);

        this.Upload = Upload;
    }

    /**
     * @param {Object=} params
     * @param {Property=} property
     * @returns {Promise.<PaginationCollection<Guest>>}
     */
    get(params = {}, property = null) {

        const url = this.url({
            propertyId: property ? property.id : 0
        });
        const ignoreLB = params.hasOwnProperty('searchValue');
        return this.http
            .get(url, {
                headers: this.authHeaders,
                params,
                ignoreLoadingBar: ignoreLB
            })
            .then(this.getPaginationCollector())
            .catch(this.getErrorHandler());
    }

    reservationGet(params = {}, reservation = {}) {

        const url = this.url({}, `/reservation/${reservation.id}/guests`);

        return this.http.get(url, {
                headers: this.authHeaders,
                params
            })
            .then(this.getMassModeler())
            .catch(this.getErrorHandler());

    }

    /**
     * @param {Guest} guest
     * @param property
     * @returns {Promise.<Guest>}
     */
    show(guest, property = null) {

        const url = this.guestUrl(guest, property);

        return this.http
            .get(url, {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    update(guest, property = null) {

        const url = this.guestUrl(guest, property);

        const guestToSave = guest.toObjectExcept('country', 'city', 'property', 'nationality');

        if (guest.accommodations) guest.accommodations = guest.accommodations.map(acc => {
            acc['replace_primary'] = acc['replace_primary'] || 0;

            return acc;
        });

        return this.http
            .put(url, guestToSave, {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    create(guest, property = null) {

        const url = this.url({
            propertyId: property.id
        });

        if (guest.accommodations) guest.accommodations = guest.accommodations.map(acc => {
            acc['replace_primary'] = acc['replace_primary'] || 0;

            return acc;
        });

        return this.http.post(url, guest.toObject(), {
                headers: this.authHeaders,
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    delete(property, guest) {

        const url = this.url({
            propertyId: property.id,
            id: guest.id
        });

        return this.http
            .delete(url, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    removeFromReservation(reservation, guest) {

        const url = this.url({}, `/reservation/${reservation.id}/remove-guest/${guest.id}`);

        return this.http.post(url, guest, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    export(params = {}, property = null) {

        const url = this.url({
            propertyId: property ? property.id : 0
        }) + '/export';

        return this.download(url, mapGetToPostParams(params), 'Guests.xls');
    }

    uploadPhoto(guest, property = null) {

        const url = this.guestUrl(guest, property);

        if (guest.photo_file instanceof File) {
            return this.Upload
                .upload({
                    url: url + '/photo',
                    headers: this.authHeaders,
                    data: guest.toObject('photo_file'),
                })
                .then(this.getDataExtractor())
                .catch(this.getErrorHandler());
        }
    }

    deletePhoto(guest, property) {

        const url = this.guestUrl(guest, property);

        return this.http
            .delete(url + '/photo', {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    statistics(guest, property = null) {

        const url = `${this.guestUrl(guest, property)}/statistics`;

        return this.http
            .get(url, {
                headers: this.authHeaders
            })
            .then(this.getDataExtractor())
            .catch(this.getErrorHandler());
    }

    reservationSources(guest, property = null) {

        const url = `${this.guestUrl(guest, property)}/reservation-sources`;

        return this.http
            .get(url, {
                headers: this.authHeaders
            })
            .then(response => this.extractData(response).map(attributes => new Reservation(attributes)))
            .catch(this.getErrorHandler());
    }

    updateGuestStatus(guest, property = null) {

        const url = `${this.guestUrl(guest, property)}/guest-status`;

        return this.http
            .post(url, guest, {
                headers: this.authHeaders,
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    guestUrl(guest, property = null) {

        return this.url({
            propertyId: property ? property.id : 0,
            id: guest.id
        });
    }

    typeahead(params = {}, property) {

        const url = this.url({
            propertyId: property.id
        });

        return this.http
            .post(url + '/typeahead', params, {
                headers: this.authHeaders,
                ignoreLoadingBar: true
            })
            .then(this.getMassModeler())
            .catch(this.getErrorHandler());
    }

    validation(guest, property) {

        const url = this.url({
            propertyId: property.id
        });

        return this.http
            .post(url + '/validation', guest.toObjectExcept('country', 'city', 'property', 'nationality'), {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    approveProfileImage(guest, reservation) {

        const url = this.url({}, `/reservation/${reservation.id}/guest/${guest.id}/attach-profile-photo`);

        return this.http.post(url, guest, {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler);
    }

    attachAccommodation(guest, accommodation) {

        const url = this.url({}, `/guest/${guest.id}/attach-accommodation/accommodation/${accommodation.id}`);

        return this.http.post(url, {
                replace_primary: accommodation['replace_primary'] || 0,
                is_child: !!guest.is_child
            }, {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    detachAccommodation(guest, accommodation) {

        const url = this.url({}, `/guest/${guest.id}/detach-accommodation/accommodation/${accommodation.id}`);

        return this.http.post(url, {
                is_child: !!guest.is_child
            }, {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    /**
     * @param {Object=} params
     * @param {Property=} property
     * @returns {Promise.<Guest>[]}
     */
    getIdents(params = {}, property = null) {

        const url = this.url({
            propertyId: property ? property.id : 0
        });

        return this.http
            .get(url + '/idents', {
                headers: this.authHeaders,
                params
            })
            .then(this.getMassModeler())
            .catch(this.getErrorHandler());
    }

    /**
     * @todo move children storing to new service (?)
     */

    saveChild(model, property) {

        if (model.accommodations) model.accommodations = model.accommodations.map(acc => {
            acc['replace_primary'] = acc['replace_primary'] || 0;
            return acc;
        });

        return !model.id ? this.storeChild(model, property) : this.updateChild(model, property);
    }

    storeChild(model, property) {

        const url = this.url({}, `/property/${property.id}/children`);

        return this.http.post(url, model, {

                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    updateChild(model, property) {

        const url = this.url({}, `/property/${property.id}/children/${model.id}`);

        return this.http.put(url, model, {
                headers: this.authHeaders
            })
            .then(this.getModeler())
            .catch(this.getErrorHandler());
    }

    setPrimaryGuest(reservation, guest) {

        const url = this.url({}, `/reservation/${reservation.id}/set-primary-guest/${guest.id}`);

        return this.http.post(url, {}, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

    bulkRemoveFromReservation(reservation, guests) {

        const url = this.url({}, `/reservation/${reservation.id}/bulk-remove-guests`);

        return this.http.post(url, {guests: guests}, {
                headers: this.authHeaders
            })
            .then(this.getStatusExtractor())
            .catch(this.getErrorHandler());
    }

}



// WEBPACK FOOTER //
// ./src/pms-api/services/guests.service.js